const cloneDeep = require("lodash.clonedeep");

export const capitalizeFirstLetter = (letter: string): string =>
  letter.charAt(0).toUpperCase() + letter.slice(1);

/**
 * @description Добавить Российскую маску к номеру телефона
 * @param phone: string
 * @return string - +7 (000) 000 00 00
 */
export const addPhoneMask = (phone: string): string => {
  return `+${phone.slice(0, 1)} (${phone.slice(1, 4)}) ${phone.slice(
    4,
    7
  )} ${phone.slice(7, 9)} ${phone.slice(9, 11)}`;
};

/**
 * @description Убрать маску с номера телефона
 * @param phone: string
 */
export const removePhoneMask = (phone: string): string => {
  return phone.replace(/[^0-9]/g, "");
};

export const dynamicImport = async (importFunction: Function) => {
  try {
    return importFunction();
  } catch (e) {
    console.error(e);
  }
};

/**
 * @description Функция-ждун
 * @param timeout
 */
export const sleep = async (timeout: number): Promise<undefined> => {
  return new Promise((resolve) => {
    setTimeout(resolve, timeout);
  });
};

/**
 * @description Получить уникальные элементы из массива
 * @param arr
 */
export const uniqueArray = <T>(arr: T[]): T[] => {
  const set = Array.from(new Set(arr));
  if (set.length) return [...set];
  return [];
};

/**
 * @description Глубокое клонирование массива или объекта любой вложенности
 * !Внимание! не клонирует объекты, которые не являются JSON-объектами
 * На подобии классов HTMLElement или FileReader
 * @param json
 */
export const deepClone = <T>(json: T) => {
  return cloneDeep(json);
};

/**
 * @description Debounce функция, позволяет выполять переднный в неё callback через n секунд
 * после последнего вызова
 */
export const debounce = (() => {
  let timeout: any = 0;
  return (func: () => void, wait: number) => {
    clearTimeout(timeout);
    timeout = setTimeout(func, wait);
  };
})();

/**
 * @description Функция подставляющая корректную подпись к году на русском или анлийском языке
 * @param year: number | string - год (например 2010)
 * @param locale: string - язык, по дефолту русский
 */
export const yearName = (year: number | string, locale: string = "ru") => {
  const absYear = Math.abs(Number(year));

  if (Number.isNaN(absYear)) {
    throw new Error(
      'Argument in helper function "yearName" in helpFunctions.ts is not convert to number'
    );
  }

  if (locale === "en") {
    return `${year} ${absYear === 1 ? "year" : "years"}`;
  }
  // 1 год
  // 2-4: года
  // 5-20: лет
  // *1: год
  // *2-*4: года
  // *5-*0: лет

  if (absYear % 100 >= 5 && absYear % 100 <= 20) {
    return `${year} лет`;
  }

  switch (absYear % 10) {
    case 1:
      return `${year} год`;
    case 2:
    case 3:
    case 4:
      return `${year} года`;
    default:
      return `${year} лет`;
  }
};

/**
 * @description Функция подставляющая корректную подпись к месяцу на русском или анлийском языке
 * @param month: number | string - кол-во месяцев (например 1)
 * @param locale: string - язык, по дефолту русский
 */
export const getMonthText = (month: number | string, locale: string = "ru") => {
  const absYear = Math.abs(Number(month));

  if (Number.isNaN(absYear)) {
    throw new Error(
      'Argument in helper function "monthName" in helpFunctions.ts is not convert to number'
    );
  }

  if (locale === "en") {
    return `${month} ${absYear === 1 ? "month" : "months"}`;
  }
  // 1 месяц
  // 2-4: месяца
  // 5-20: месяцев
  // *1: месяц
  // *2-*4: месяца
  // *5-*0: месяцев

  if (absYear % 100 >= 5 && absYear % 100 <= 20) {
    return `${month} месяцев`;
  }

  switch (absYear % 10) {
    case 1:
      return `${month} месяц`;
    case 2:
    case 3:
    case 4:
      return `${month} месяца`;
    default:
      return `${month} месяцев`;
  }
};

/**
 * @description Получение текущего unix timestamp в секундах
 * с возможностью смещения на offset
 * @param offset: number
 */
export const getDateNowInSeconds = (offset: number = 0): number => {
  return Math.trunc(Date.now() / 1000) + offset;
};

/**
 * @description Форматирование времени в строковом формате (h:mm:ss)
 * @param seconds: number - кол-во секунд
 * @param format: string - возвращаемые части из времени формата (h:mm:ss)
 */
export const formattingSecondsToStringTime = (
  seconds: number,
  format: string = "h:m:s"
): string => {
  if (seconds < 0) {
    throw new Error("Second can't be less than 0");
  }
  const formattingToTwoSymbols = (num: number) => String(num + 100).slice(-2);
  const hoursBlock = Math.trunc(seconds / 60 / 60);
  const minutesBlock = Math.trunc(seconds / 60) - hoursBlock * 60;
  const secondsBlock = seconds % 60;
  return format
    .replace("h", String(hoursBlock))
    .replace("mm", formattingToTwoSymbols(minutesBlock))
    .replace("ss", formattingToTwoSymbols(secondsBlock))
    .replace("m", String(minutesBlock))
    .replace("s", String(secondsBlock));
};

/**
 * @description Склейка соответствующих элементов из 2 массивов
 * @typeParam <T1, T2> - Дженерики функции
 * @param a: T1 - первый массив переданного типа
 * @param b: T2 - второй массив переданного типа
 * @param searchCondition: (arg1: T1, arg2: T2) => boolean - функция - условие поиска
 * @return (T1 & T2)[] - массив состоящий из склееных объектов по переданному условию
 */
export const joinArrays = <T1, T2>(
  a: T1[],
  b: T2[],
  searchCondition: (arg1: T1, arg2: T2) => boolean
): (T1 & T2)[] => {
  let foundItem;
  return a.reduce<(T1 & T2)[]>((acc: (T1 & T2)[], current: T1): (T1 & T2)[] => {
    foundItem = b.find((item) => searchCondition(current, item));

    if (foundItem) acc.push({ ...foundItem, ...current });

    return acc;
  }, [] as (T1 & T2)[]);
};

/**
 * @description Функция для ожидания выполнения переданного условия
 * @param condition: () => boolean - Функция c условием
 * @param interval: number - Интервал проверки
 * @return Promise<void>
 */
export const waitingTheCondition = (
  condition: () => boolean,
  interval: number
): Promise<void> => {
  return new Promise((resolve) => {
    const timer = setInterval(() => {
      if (condition()) {
        clearInterval(timer);
        resolve();
      }
    }, interval);
  });
};

/**
 * @description Сортировка массива объектов по массиву ключей
 * @param sortingArray: T[] - сортируемые массив объектов
 * @param sortingRule: string[] - массив строк - ключей
 * @param searchCondition: (arg1: T1, arg2: string) => boolean - функция - условие сравнения
 * @return T[] - упорядоченный в соответствие с массивом ключей
 */
export const sortArrayByKeys = <T>(
  sortingArray: T[],
  sortingRule: string[],
  searchCondition: (arg1: T, arg2: string) => boolean
) => {
  if (!Array.isArray(sortingArray)) {
    console.error("sortingArray is not array");
    return [];
  }
  if (!Array.isArray(sortingArray)) {
    console.error("sortingRule is not array");
    return [];
  }
  let foundItem: T | undefined;
  return sortingRule.reduce((acc, cur) => {
    foundItem = sortingArray.find((item) => searchCondition(item, cur));

    if (foundItem) {
      acc.push(foundItem);
    }
    return acc;
  }, [] as T[]);
};

export function replaceURLs(message: string) {
  if (!message) return;

  const urlRegex = /(((https?:\/\/)|(www\.))[^\s]+)/g;

  return message.replace(urlRegex, function (url) {
    let href = url;
    if (!href.match("^https?://")) {
      href = "http://" + href;
    }
    return `<a href="${href}" target="_blank" rel="noopener noreferrer">${url}</a>`;
  });
}

export function getRecaptchaSiteKey(): string {
  return process.env.VUE_APP_RECAPTCHA_SITE_KEY || '6LerU9ghAAAAAEyQ78RQX1a22IDCT_ZmHU9KI91b';
}

export function getRecaptchaIgnoreKey(): string | null {
  return process.env.VUE_APP_RECAPTCHA_IGNORE_KEY || null;
}

export function getCheckListDownloadUrl(leadId: number): string {
  if (process.env.VUE_APP_CHECKLIST_DOWNLOAD_URL) {
    return `${process.env.VUE_APP_CHECKLIST_DOWNLOAD_URL}?leadId=${leadId}`;
  }

  return `https://lk-api.akbars-dom.ru/site/download-checklist?leadId=${leadId}`;
}

export function getPropertyRegisterLink(): string {
  return process.env.VUE_APP_PROPERTY_REGISTER_LINK || 'https://yookassa.ru/my/i/ZxtOSWUfyJGT/l';
}